// ROULETTE.EXE — one wheel, one zero, thirty-seven pockets.
//
// The wheel is the European order, clockwise from the zero, drawn from ROULETTE_WHEEL rather than
// typed out here, so the picture and the contract cannot drift apart. It free-spins while the round
// is in the air and then decelerates to put the settled pocket under the pointer at the top — the
// pocket the chain picked, never one this component rolled.
//
// The felt below is the old layout: the zero, the three-by-twelve grid, the column bets, the
// dozens and the even-money row. Every click toggles pockets in one Set; the outside bets toggle
// their whole group. What you cover is what the contract is told, as a 37-bit mask.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealRoulette } from 'lib/casino';
import {
  ROULETTE_POCKETS,
  ROULETTE_WHEEL,
  rouletteChanceWad,
  rouletteColor,
  rouletteMask,
  roulettePayoutMultBps,
} from 'lib/casino-math';
import sfx from 'lib/sfx';
import { AutoPanel, useAuto } from './auto';
import { useBet, useStakeCap } from './state';
import {
  FONT,
  HouseRules,
  Outcome,
  Panel,
  PlayButton,
  StakeBox,
  T,
  fmtChip,
  fmtPctFromWad,
  parseUnits,
  xpButton,
} from './ui';

const BPS = BigInt(10000);
const ALL_IN_BPS = BigInt(10500);
const STEP = 360 / ROULETTE_POCKETS;
const SPIN_SECONDS = 12; // the free-spin leg, restarted whenever a round is still in the air
const LAND_SECONDS = 3.9; // the deceleration onto the settled pocket
const MAX_COVERED = 35; // 36 and up is TooCheap() at the door

// ── the wheel, computed once: every pocket's sector path and label anchor ─────
const R_OUT = 104;
const R_IN = 72;
const CX = 120;
const CY = 120;

function polar(r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

const POCKETS = ROULETTE_WHEEL.map((pocket, i) => {
  const mid = i * STEP;
  const a1 = mid - STEP / 2;
  const a2 = mid + STEP / 2;
  const [ox1, oy1] = polar(R_OUT, a1);
  const [ox2, oy2] = polar(R_OUT, a2);
  const [ix2, iy2] = polar(R_IN, a2);
  const [ix1, iy1] = polar(R_IN, a1);
  const [lx, ly] = polar((R_OUT + R_IN) / 2, mid);
  return {
    pocket,
    index: i,
    color: rouletteColor(pocket),
    d: `M${ox1.toFixed(2)} ${oy1.toFixed(2)}A${R_OUT} ${R_OUT} 0 0 1 ${ox2.toFixed(2)} ${oy2.toFixed(2)}L${ix2.toFixed(2)} ${iy2.toFixed(2)}A${R_IN} ${R_IN} 0 0 0 ${ix1.toFixed(2)} ${iy1.toFixed(2)}Z`,
    lx,
    ly,
    mid,
  };
});

const FILL = { green: '#0b7a3a', red: '#c8102e', black: '#141414' };

// ── the felt layout ──────────────────────────────────────────────────────────
const NUMBERS = [];
for (let row = 0; row < 3; row += 1) {
  for (let col = 0; col < 12; col += 1) NUMBERS.push(col * 3 + (3 - row));
}
const RANGE = (lo, hi) => Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
const COLUMN_OF = row => RANGE(1, 36).filter(n => n % 3 === (3 - row) % 3);

const OUTSIDE = [
  { key: 'd1', label: '1st 12', span: 4, group: RANGE(1, 12) },
  { key: 'd2', label: '2nd 12', span: 4, group: RANGE(13, 24) },
  { key: 'd3', label: '3rd 12', span: 4, group: RANGE(25, 36) },
];
const EVEN_MONEY = [
  { key: 'lo', label: '1–18', group: RANGE(1, 18) },
  { key: 'even', label: 'EVEN', group: RANGE(1, 36).filter(n => n % 2 === 0) },
  { key: 'red', label: 'RED', group: RANGE(1, 36).filter(n => rouletteColor(n) === 'red'), swatch: 'red' },
  { key: 'black', label: 'BLACK', group: RANGE(1, 36).filter(n => rouletteColor(n) === 'black'), swatch: 'black' },
  { key: 'odd', label: 'ODD', group: RANGE(1, 36).filter(n => n % 2 === 1) },
  { key: 'hi', label: '19–36', group: RANGE(19, 36) },
];

export default function Roulette({ casino }) {
  const [amount, setAmount] = useState('5');
  const [covered, setCovered] = useState(() => new Set([17]));
  const [pocket, setPocket] = useState(null);
  const [deg, setDeg] = useState(0);
  const [glide, setGlide] = useState({ dur: 0, ease: 'linear' });
  const bet = useBet('roulette', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';
  const pending = bet.phase === 'approving' || bet.phase === 'placing' || bet.phase === 'waiting' || bet.phase === 'settling';

  const n = covered.size;
  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BPS) / ALL_IN_BPS;
  const multBps = n > 0 ? roulettePayoutMultBps(n) : BigInt(0);
  const payout = (stake * multBps) / BPS;
  const chanceWad = n > 0 ? rouletteChanceWad(n) : BigInt(0);
  const tooCheap = n > MAX_COVERED;
  const mask = useMemo(() => rouletteMask(Array.from(covered)), [covered]);

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('roulette', Number(multBps), casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && n > 0 && !tooCheap && !overCap;
  const auto = useAuto(bet, { allIn, args: [mask], canPlay, balance: casino.balance });
  const playArgs = [mask];

  // The settled pocket, read back from the cabinet's own view of the round.
  useEffect(() => {
    let alive = true;
    setPocket(null);
    if (!bet.result) return undefined;
    revealRoulette(bet.result.id, bet.result.randomness)
      .then(p => {
        if (alive) setPocket(Number(p));
      })
      .catch(() => {
        // The round is settled either way; the receipt below is the authority. Leaving the
        // pocket null keeps the wheel spun-down and silent rather than showing a wrong number.
        if (alive) setPocket(null);
      });
    return () => {
      alive = false;
    };
  }, [bet.result]);

  // Leg one: free-spin for as long as the round is in the air.
  useEffect(() => {
    if (!pending) return;
    setGlide({ dur: SPIN_SECONDS, ease: 'linear' });
    setDeg(d => d + 360 * SPIN_SECONDS);
    if (sfx.wheelSpin) sfx.wheelSpin(SPIN_SECONDS * 1000, 60);
  }, [pending]);

  // Leg two: decelerate so the settled pocket comes to rest under the pointer.
  const dropRef = useRef(null);
  useEffect(() => {
    if (pocket === null) return undefined;
    const index = ROULETTE_WHEEL.indexOf(pocket);
    if (index < 0) return undefined; // a pocket outside the wheel would be a contract change, not a spin
    setDeg(current => {
      const base = -index * STEP;
      const turns = Math.ceil((current + 360 * 3 - base) / 360);
      return base + 360 * turns;
    });
    setGlide({ dur: LAND_SECONDS, ease: 'cubic-bezier(0.12, 0.66, 0.1, 1)' });
    dropRef.current = setTimeout(() => {
      dropRef.current = null;
      if (sfx.ballDrop) sfx.ballDrop();
    }, LAND_SECONDS * 1000);
    return () => {
      if (dropRef.current !== null) {
        clearTimeout(dropRef.current);
        dropRef.current = null;
      }
    };
  }, [pocket]);

  const toggleNumber = num => {
    if (locked) return;
    setCovered(prev => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num);
      else next.add(num);
      return next;
    });
    if (sfx.chip) sfx.chip();
  };

  const toggleGroup = group => {
    if (locked) return;
    setCovered(prev => {
      const next = new Set(prev);
      const all = group.every(num => next.has(num));
      group.forEach(num => (all ? next.delete(num) : next.add(num)));
      return next;
    });
    if (sfx.chip) sfx.chip();
  };

  const clear = () => {
    if (locked) return;
    setCovered(new Set());
  };

  const landed = pocket === null ? null : rouletteColor(pocket);

  return (
    <Div>
      <div className="rl__stage">
        <div className="rl__wheelwrap">
          <div className="rl__pointer" aria-hidden="true" />
          <svg className="rl__wheel" viewBox="0 0 240 240" role="img" aria-label="Roulette wheel">
            <defs>
              <radialGradient id="rl-rim" cx="38%" cy="28%">
                <stop offset="0%" stopColor="#fff3c4" />
                <stop offset="45%" stopColor="#ffd86b" />
                <stop offset="100%" stopColor="#8a6a10" />
              </radialGradient>
              <radialGradient id="rl-hub" cx="38%" cy="30%">
                <stop offset="0%" stopColor="#ffe9a8" />
                <stop offset="60%" stopColor="#c9a227" />
                <stop offset="100%" stopColor="#5a4200" />
              </radialGradient>
            </defs>
            <circle cx={CX} cy={CY} r="116" fill="#2a1a06" />
            <circle cx={CX} cy={CY} r="112" fill="url(#rl-rim)" />
            <circle cx={CX} cy={CY} r={R_OUT + 2} fill="#3b2a00" />
            <g
              className="rl__rotor"
              style={{ transform: `rotate(${deg}deg)`, transitionDuration: `${glide.dur}s`, transitionTimingFunction: glide.ease }}
            >
              {POCKETS.map(p => (
                <g key={p.pocket}>
                  <path d={p.d} fill={FILL[p.color]} stroke="#d8c27a" strokeWidth="0.6" />
                  <text
                    x={p.lx}
                    y={p.ly}
                    className="rl__num"
                    transform={`rotate(${p.mid} ${p.lx.toFixed(2)} ${p.ly.toFixed(2)})`}
                  >
                    {p.pocket}
                  </text>
                </g>
              ))}
              <circle cx={CX} cy={CY} r={R_IN} fill="url(#rl-hub)" stroke="#5a4200" strokeWidth="1.5" />
              <circle cx={CX} cy={CY} r="34" fill="#0b5d33" stroke="#c9a227" strokeWidth="2" />
              {[0, 60, 120, 180, 240, 300].map(a => {
                const [x, y] = polar(52, a);
                return <circle key={a} cx={x} cy={y} r="3" fill="#5a4200" opacity="0.8" />;
              })}
              <text x={CX} y={CY + 5} className="rl__hub">
                888
              </text>
            </g>
            <g className={`rl__ball${pending ? ' orbit' : ''}${pocket === null ? '' : ' rest'}`}>
              <circle cx={CX} cy={CY - (R_OUT + R_IN) / 2} r="6.5" fill="#fffbe6" stroke="#8a8a7a" strokeWidth="0.8" />
              <circle cx={CX - 2} cy={CY - (R_OUT + R_IN) / 2 - 2} r="2" fill="#fff" opacity="0.9" />
            </g>
          </svg>
        </div>

        <div className="rl__readout">
          <div className="rl__title">THE BALL</div>
          <div className={`rl__pocket rl__pocket--${landed || 'idle'}`}>{pocket === null ? '—' : pocket}</div>
          <div className="rl__caption">
            {pocket === null
              ? pending
                ? 'no more bets'
                : `${n} pocket${n === 1 ? '' : 's'} covered`
              : `${landed.toUpperCase()} ${pocket} · ${covered.has(pocket) ? 'yours' : 'not yours'}`}
          </div>
        </div>
      </div>

      <Panel>
        <div className="rl__felt">
          <div className="rl__grid">
            <button
              type="button"
              className={`rl__cell rl__cell--zero${covered.has(0) ? ' on' : ''}`}
              onClick={() => toggleNumber(0)}
              disabled={locked}
            >
              0{covered.has(0) ? <i className="rl__chip" /> : null}
            </button>

            {NUMBERS.map(num => (
              <button
                key={num}
                type="button"
                className={`rl__cell rl__cell--${rouletteColor(num)}${covered.has(num) ? ' on' : ''}${pocket === num ? ' hit' : ''}`}
                onClick={() => toggleNumber(num)}
                disabled={locked}
              >
                {num}
                {covered.has(num) ? <i className="rl__chip" /> : null}
              </button>
            ))}

            {[0, 1, 2].map(row => {
              const group = COLUMN_OF(row);
              const on = group.every(num => covered.has(num));
              return (
                <button
                  key={`col${row}`}
                  type="button"
                  className={`rl__cell rl__cell--out${on ? ' on' : ''}`}
                  style={{ gridColumn: 14, gridRow: row + 1 }}
                  onClick={() => toggleGroup(group)}
                  disabled={locked}
                >
                  2:1
                </button>
              );
            })}

            {OUTSIDE.map((o, i) => {
              const on = o.group.every(num => covered.has(num));
              return (
                <button
                  key={o.key}
                  type="button"
                  className={`rl__cell rl__cell--out${on ? ' on' : ''}`}
                  style={{ gridColumn: `${2 + i * 4} / span ${o.span}`, gridRow: 4 }}
                  onClick={() => toggleGroup(o.group)}
                  disabled={locked}
                >
                  {o.label}
                </button>
              );
            })}

            {EVEN_MONEY.map((o, i) => {
              const on = o.group.every(num => covered.has(num));
              return (
                <button
                  key={o.key}
                  type="button"
                  className={`rl__cell rl__cell--out${on ? ' on' : ''}${o.swatch ? ` rl__cell--${o.swatch}` : ''}`}
                  style={{ gridColumn: `${2 + i * 2} / span 2`, gridRow: 5 }}
                  onClick={() => toggleGroup(o.group)}
                  disabled={locked}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rl__bar">
          <span className="rl__covered">
            COVERED <b>{n}</b> / {ROULETTE_POCKETS}
          </span>
          <button type="button" className="rl__clear" onClick={clear} disabled={locked || n === 0}>
            CLEAR
          </button>
        </div>

        <div className="rl__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">{n > 0 ? `${(Number(multBps) / 10000).toFixed(2)}x` : '—'}</span>
          </div>
          <div>
            <span className="k">Chance</span>
            <span className="v">{n > 0 ? fmtPctFromWad(chanceWad) : '—'}</span>
          </div>
          <div>
            <span className="k">Returns</span>
            <span className="v">
              {fmtChip(payout)} {CHIP.symbol}
            </span>
          </div>
        </div>

        {tooCheap ? (
          <div className="rl__warn">
            That cover pays 1.00x or less, and the cabinet refuses it. Cover fewer pockets.
          </div>
        ) : null}
        {n === 0 ? <div className="rl__warn">Cover at least one pocket.</div> : null}

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="SPIN"
          onClick={() => bet.play(allIn, playArgs)}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        <AutoPanel auto={auto} practice={casino.practice} />

        {bet.phase === 'stuck' ? (
          <div className="rl__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset}>
          {pocket === null ? null : (
            <div className="rl__receipt">
              the ball landed in <b>{landed} {pocket}</b>
            </div>
          )}
        </Outcome>

        <HouseRules
          practice={casino.practice}
          edge="This game is not even-money. The wheel pays 97.30%, 5% fee on top, ~92.4% expected return."
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .rl__stage {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    background: radial-gradient(ellipse at 50% 28%, #1a8a4c, ${T.felt} 55%, ${T.feltDark} 100%);
    border: 4px solid #5a3a12;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px ${T.goldDeep}, inset 0 0 40px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.5);
    padding: 14px 16px;
  }
  .rl__wheelwrap {
    position: relative;
    width: 232px;
    height: 232px;
    flex-shrink: 0;
  }
  .rl__wheel {
    width: 100%;
    height: 100%;
    display: block;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.55));
  }
  .rl__rotor {
    transform-origin: 120px 120px;
    transition-property: transform;
  }
  .rl__num {
    font: bold 9px ${FONT.web};
    fill: #fffbe6;
    text-anchor: middle;
    dominant-baseline: middle;
  }
  .rl__hub {
    font: bold 15px ${FONT.serif};
    fill: ${T.gold};
    text-anchor: middle;
    letter-spacing: 1px;
  }
  .rl__pointer {
    position: absolute;
    top: -2px;
    left: 50%;
    width: 0;
    height: 0;
    margin-left: -9px;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 18px solid ${T.gold};
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.6));
    z-index: 2;
  }
  .rl__ball {
    transform-origin: 120px 120px;
  }
  .rl__ball.orbit {
    animation: rl-orbit 1.15s linear infinite reverse;
  }
  .rl__ball.rest {
    animation: rl-drop 0.7s cubic-bezier(0.3, 1.5, 0.5, 1) 1;
  }
  @keyframes rl-orbit {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes rl-drop {
    from {
      transform: scale(1.5) translateY(-6px);
    }
    to {
      transform: scale(1) translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .rl__rotor {
      transition-duration: 0s !important;
    }
    .rl__ball.orbit,
    .rl__ball.rest {
      animation: none;
    }
  }

  .rl__readout {
    flex: 1;
    min-width: 150px;
    text-align: center;
  }
  .rl__title {
    font: bold 10px ${FONT.serif};
    letter-spacing: 5px;
    color: ${T.gold};
  }
  .rl__pocket {
    margin: 6px auto 0;
    width: 88px;
    height: 88px;
    border-radius: 50%;
    border: 3px double ${T.goldDeep};
    display: flex;
    align-items: center;
    justify-content: center;
    font: bold 38px ${FONT.serif};
    color: #fffbe6;
    background: #0a3d22;
    box-shadow: inset 0 0 18px rgba(0, 0, 0, 0.6);
  }
  .rl__pocket--red {
    background: ${FILL.red};
  }
  .rl__pocket--black {
    background: ${FILL.black};
  }
  .rl__pocket--green {
    background: ${FILL.green};
  }
  .rl__caption {
    margin-top: 7px;
    font: 10px ${FONT.web};
    color: ${T.cream};
    letter-spacing: 1px;
  }

  .rl__felt {
    margin: 10px 0 0;
    padding: 8px;
    background: linear-gradient(180deg, #0e6c3c, #084a28);
    border: 2px solid ${T.goldDeep};
    overflow-x: auto;
  }
  .rl__grid {
    display: grid;
    grid-template-columns: 32px repeat(12, minmax(24px, 1fr)) 38px;
    grid-auto-rows: 26px;
    gap: 2px;
    min-width: 420px;
  }
  .rl__cell {
    position: relative;
    border: 1px solid rgba(255, 216, 107, 0.55);
    color: #fff;
    font: bold 11px ${FONT.web};
    cursor: pointer;
    padding: 0;
    background: transparent;
  }
  .rl__cell:disabled {
    cursor: default;
  }
  .rl__cell--zero {
    grid-column: 1;
    grid-row: 1 / span 3;
    background: ${FILL.green};
    font-size: 15px;
  }
  .rl__cell--red {
    background: ${FILL.red};
  }
  .rl__cell--black {
    background: ${FILL.black};
  }
  .rl__cell--out {
    background: rgba(0, 0, 0, 0.25);
    font-size: 10px;
    letter-spacing: 1px;
  }
  .rl__cell--out.rl__cell--red {
    background: ${FILL.red};
  }
  .rl__cell--out.rl__cell--black {
    background: ${FILL.black};
  }
  .rl__cell:not(:disabled):hover {
    border-color: ${T.gold};
    box-shadow: inset 0 0 0 1px ${T.gold};
  }
  .rl__cell.on {
    box-shadow: inset 0 0 0 2px ${T.gold};
  }
  .rl__cell.hit {
    animation: rl-hit 1.1s ease-out 3;
  }
  @keyframes rl-hit {
    0%,
    100% {
      box-shadow: inset 0 0 0 2px ${T.gold};
    }
    50% {
      box-shadow: inset 0 0 0 3px #fff, 0 0 12px ${T.gold};
    }
  }
  .rl__chip {
    position: absolute;
    right: 1px;
    bottom: 1px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff3c4, #ffd86b 45%, #b8860b 100%);
    border: 1px solid #7a5c00;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  }

  .rl__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .rl__covered {
    font: 11px ${FONT.mono};
    color: #222;
    letter-spacing: 1px;
  }
  .rl__covered b {
    color: ${T.navy};
    font-size: 13px;
  }
  .rl__clear {
    ${xpButton};
    min-width: 60px;
    min-height: 21px;
    padding: 0 8px;
  }

  .rl__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .rl__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .rl__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .rl__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .rl__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .rl__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
  .rl__receipt {
    font: 11px ${FONT.ui};
    margin-bottom: 4px;
  }
`;
